import React from 'react';
import { LotCalcule } from '../types';

interface GanttBarProps {
  lot: LotCalcule;
  projetDebut: Date;
  projetFin: Date;
}

const statutColors: Record<string, string> = {
  non_demarre: 'bg-gray-300',
  en_cours: 'bg-violet-500',
  termine: 'bg-green-500',
  en_retard: 'bg-red-500',
};

const GanttBar: React.FC<GanttBarProps> = ({ lot, projetDebut, projetFin }) => {
  const totalDuration = projetFin.getTime() - projetDebut.getTime();
  if (totalDuration <= 0) return null;

  const lotDebut = new Date(lot.dateDebutReelle || lot.dateDebutPrevue);
  const lotFin = new Date(lot.dateFinReelle || lot.dateFinPrevue);

  const leftPct = Math.max(0, ((lotDebut.getTime() - projetDebut.getTime()) / totalDuration) * 100);
  const widthPct = Math.max(2, ((lotFin.getTime() - lotDebut.getTime()) / totalDuration) * 100);

  const barColor = lot.enRetard ? statutColors.en_retard : (statutColors[lot.statut] || statutColors.non_demarre);

  // Barre prévue (en dessous, plus fine)
  const prevueDebut = new Date(lot.dateDebutPrevue);
  const prevueFin = new Date(lot.dateFinPrevue);
  const prevueLeftPct = Math.max(0, ((prevueDebut.getTime() - projetDebut.getTime()) / totalDuration) * 100);
  const prevueWidthPct = Math.max(2, ((prevueFin.getTime() - prevueDebut.getTime()) / totalDuration) * 100);
  const hasReelDates = lot.dateDebutReelle || lot.dateFinReelle;

  return (
    <div className="flex items-center gap-3 py-2">
      {/* Label */}
      <div className="w-48 flex-shrink-0">
        <div className="text-sm font-medium text-gray-900 truncate">{lot.nom}</div>
        <div className="text-xs text-gray-500 truncate">{lot.entreprise}</div>
      </div>

      {/* Bar area */}
      <div className="flex-1 relative h-10">
        {/* Barre prévue (si dates réelles existent) */}
        {hasReelDates && (
          <div
            className="absolute top-6 h-1.5 bg-gray-300 rounded-full opacity-60"
            style={{ left: `${prevueLeftPct}%`, width: `${prevueWidthPct}%` }}
            title={`Prévu : ${prevueDebut.toLocaleDateString('fr-FR')} - ${prevueFin.toLocaleDateString('fr-FR')}`}
          />
        )}

        {/* Barre principale */}
        <div
          className={`absolute top-1 h-5 ${barColor} rounded-md shadow-sm flex items-center justify-center`}
          style={{
            left: `${leftPct}%`,
            width: `${widthPct}%`,
            ...(lot.enRetard ? {
              backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(255,255,255,0.3) 3px, rgba(255,255,255,0.3) 6px)',
            } : {}),
          }}
          title={`${lotDebut.toLocaleDateString('fr-FR')} - ${lotFin.toLocaleDateString('fr-FR')}${lot.enRetard ? ' (en retard)' : ''}`}
        >
          <span className="text-[10px] text-white font-medium truncate px-1">
            {lot.pourcentageAvancement}%
          </span>
        </div>
      </div>
    </div>
  );
};

export default GanttBar;
